import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import {
  api as accountsApi,
  firmsApi,
  type Account,
  type Firm,
  type ManualTrade,
  type ManualTradeInput,
} from "../lib/api";

/**
 * Create + edit form for the manual trade log.
 *
 * Account dropdown is sourced from /accounts. The chosen account drives the
 * prop_firm field (snapshotted onto the row), keeping the two consistent and
 * sparing the user from picking it twice.
 *
 * SL/TP are optional. An empty string is sent as `null` on update so the
 * attribute is removed from the underlying DDB row.
 */
export default function TradeLogForm({
  title,
  initial,
  onSubmit,
  onCancel,
}: {
  title: string;
  initial?: ManualTrade;
  onSubmit: (input: ManualTradeInput) => Promise<void>;
  onCancel: () => void;
}) {
  const [entryDate, setEntryDate] = useState<string>(
    initial?.entry_date
      ? toInputDateTime(safeParseDate(initial.entry_date))
      : toInputDateTime(new Date()),
  );
  const [accountId, setAccountId] = useState(initial?.account_id ?? "");
  const [propFirm, setPropFirm] = useState(initial?.prop_firm ?? "");
  const [instrument, setInstrument] = useState(initial?.instrument ?? "");
  const [side, setSide] = useState<"buy" | "sell">(initial?.side ?? "buy");
  const [size, setSize] = useState<string>(initial ? String(initial.size) : "");
  const [entryPrice, setEntryPrice] = useState<string>(
    initial ? String(initial.entry_price) : "",
  );
  const [stopLoss, setStopLoss] = useState<string>(
    initial?.stop_loss != null ? String(initial.stop_loss) : "",
  );
  const [targetProfit, setTargetProfit] = useState<string>(
    initial?.target_profit != null ? String(initial.target_profit) : "",
  );
  const [pnl, setPnl] = useState<string>(initial ? String(initial.pnl) : "");
  const [strategy, setStrategy] = useState(initial?.strategy ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [firms, setFirms] = useState<Firm[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Track mount status so an in-flight refresh after unmount doesn't setState.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Single fetcher used on mount AND on later refresh triggers (tab focus,
  // dropdown open). Picks up active/inactive flips made elsewhere without
  // making the user close and reopen the form.
  const refresh = useCallback(async () => {
    try {
      const [accs, fms] = await Promise.all([
        accountsApi.list(),
        firmsApi.list({ includeInactive: true }),
      ]);
      if (!mountedRef.current) return;
      setAccounts(accs);
      setFirms(fms);
      setLoadError(null);
    } catch (e: unknown) {
      if (!mountedRef.current) return;
      setLoadError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void refresh();
    // Auto-sync when the tab regains focus — picks up changes made on
    // /accounts or /firms in another tab without a manual reload.
    const onFocus = () => void refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  const accountOptions = useMemo(() => {
    if (!accounts) return [];
    const active = accounts.filter((a) => a.active);
    const sorted = [...active].sort((a, b) => a.name.localeCompare(b.name));
    if (initial && !active.some((a) => a.account_id === initial.account_id)) {
      const inactive = accounts.find((a) => a.account_id === initial.account_id);
      if (inactive) sorted.unshift(inactive);
    }
    return sorted;
  }, [accounts, initial]);

  // Strategy suggestions come from the chosen account's strategies_enabled.
  // Free text is still allowed (datalist is a hint, not a constraint).
  const strategyOptions = useMemo(() => {
    const account = accounts?.find((a) => a.account_id === accountId);
    return account?.strategies_enabled ?? [];
  }, [accounts, accountId]);

  // A stored strategy may no longer be enabled on the account (e.g. it was
  // removed after the trade was logged). Keep it visible/selectable so editing
  // an unrelated field doesn't silently change it — but flag it.
  const strategyStale = strategy !== "" && !strategyOptions.includes(strategy);
  const selectableStrategies = useMemo(() => {
    const opts = [...strategyOptions];
    if (strategy && !opts.includes(strategy)) opts.unshift(strategy);
    return opts;
  }, [strategyOptions, strategy]);

  // Keep prop_firm in sync with the selected account (the trade row carries a
  // snapshot of the firm name at submit time, like ccc-uploads.firm_name).
  // Switching accounts also clears a strategy the new account doesn't allow.
  function onAccountChange(value: string) {
    setAccountId(value);
    const account = accounts?.find((a) => a.account_id === value);
    if (account) {
      setPropFirm(account.prop_firm);
      if (strategy && !account.strategies_enabled.includes(strategy)) setStrategy("");
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!accountId) {
      setError("Please pick an account.");
      return;
    }
    if (!propFirm) {
      setError("Pick an account first — that sets the prop firm.");
      return;
    }
    if (!instrument.trim()) {
      setError("Instrument is required.");
      return;
    }
    const sizeNum = Number(size);
    if (!Number.isFinite(sizeNum) || sizeNum <= 0) {
      setError("Size must be a positive number.");
      return;
    }
    const entryNum = Number(entryPrice);
    if (!Number.isFinite(entryNum)) {
      setError("Entry price must be a number.");
      return;
    }
    const pnlNum = Number(pnl);
    if (!Number.isFinite(pnlNum)) {
      setError("P/L must be a number (can be negative).");
      return;
    }

    const slNum = stopLoss.trim() === "" ? null : Number(stopLoss);
    if (slNum !== null && !Number.isFinite(slNum)) {
      setError("Stop-loss must be a number or left blank.");
      return;
    }
    const tpNum = targetProfit.trim() === "" ? null : Number(targetProfit);
    if (tpNum !== null && !Number.isFinite(tpNum)) {
      setError("Target-profit must be a number or left blank.");
      return;
    }

    const input: ManualTradeInput = {
      entry_date: toIsoZ(entryDate),
      prop_firm: propFirm.trim(),
      account_id: accountId,
      instrument: instrument.trim(),
      side,
      size: sizeNum,
      entry_price: entryNum,
      stop_loss: slNum,
      target_profit: tpNum,
      pnl: pnlNum,
      // Always send a string (possibly "") so an edit can clear the field;
      // the server treats "" as "remove the attribute".
      strategy: strategy.trim(),
      notes: notes.trim() || undefined,
    };

    setBusy(true);
    try {
      await onSubmit(input);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="form" onSubmit={submit}>
      <h2>{title}</h2>

      <label>
        Entry date
        <input
          type="datetime-local"
          value={entryDate}
          onChange={(e) => setEntryDate(e.target.value)}
          required
        />
      </label>

      <label>
        Account
        {loadError ? (
          <span className="error">Could not load accounts: {loadError}</span>
        ) : accounts === null ? (
          <select disabled>
            <option>Loading accounts…</option>
          </select>
        ) : accountOptions.length === 0 ? (
          <span className="muted">
            No active accounts. <Link to="/accounts">Add one</Link>.
          </span>
        ) : (
          <select
            value={accountId}
            onChange={(e) => onAccountChange(e.target.value)}
            onFocus={() => void refresh()}
            required
          >
            <option value="">— pick an account —</option>
            {accountOptions.map((a) => (
              <option key={a.account_id} value={a.account_id}>
                {a.name} ({a.account_number}) — {a.prop_firm}
                {a.active ? "" : " (inactive)"}
              </option>
            ))}
          </select>
        )}
      </label>

      <label>
        Prop firm
        <input value={propFirm} readOnly disabled placeholder="(picks itself when you select an account)" />
        <small>Snapshotted from the chosen account. Pick a different account to change.</small>
        {firms === null ? null : !firms.some((f) => f.name === propFirm && f.active) && propFirm ? (
          <small className="error">Heads up — this firm isn't active. Server will reject on save.</small>
        ) : null}
      </label>

      <label>
        Instrument
        <input
          value={instrument}
          onChange={(e) => setInstrument(e.target.value)}
          required
          placeholder="e.g., NQ, NAS100, MNQ"
        />
      </label>

      <label>
        Side
        <select value={side} onChange={(e) => setSide(e.target.value as "buy" | "sell")} required>
          <option value="buy">Buy</option>
          <option value="sell">Sell</option>
        </select>
      </label>

      <label>
        Size
        <input
          type="number"
          step="any"
          value={size}
          onChange={(e) => setSize(e.target.value)}
          required
        />
      </label>

      <label>
        Entry price
        <input
          type="number"
          step="any"
          value={entryPrice}
          onChange={(e) => setEntryPrice(e.target.value)}
          required
        />
      </label>

      <label>
        Stop-loss (optional)
        <input
          type="number"
          step="any"
          value={stopLoss}
          onChange={(e) => setStopLoss(e.target.value)}
          placeholder="leave blank if none"
        />
      </label>

      <label>
        Target profit (optional)
        <input
          type="number"
          step="any"
          value={targetProfit}
          onChange={(e) => setTargetProfit(e.target.value)}
          placeholder="leave blank if none"
        />
      </label>

      <label>
        P/L
        <input
          type="number"
          step="any"
          value={pnl}
          onChange={(e) => setPnl(e.target.value)}
          required
          placeholder="Negative is fine."
        />
      </label>

      <label>
        Strategy (optional)
        {!accountId ? (
          <select disabled>
            <option>Pick an account first…</option>
          </select>
        ) : (
          <select value={strategy} onChange={(e) => setStrategy(e.target.value)}>
            <option value="">— none —</option>
            {selectableStrategies.map((s) => (
              <option key={s} value={s}>
                {s}
                {strategyOptions.includes(s) ? "" : " (no longer enabled)"}
              </option>
            ))}
          </select>
        )}
        {strategyStale ? (
          <small className="error">
            This strategy isn't enabled on the account — the server will reject it
            on save. Pick another or set it to “— none —”.
          </small>
        ) : accountId && strategyOptions.length === 0 ? (
          <small>
            No strategies enabled on this account.{" "}
            <Link to="/accounts">Enable some</Link> to choose one.
          </small>
        ) : (
          <small>Only strategies enabled on the selected account.</small>
        )}
      </label>

      <label>
        Notes (optional)
        <textarea
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Trade thesis, mistakes, what you'd do differently…"
        />
      </label>

      {error && <div className="error">{error}</div>}

      <div className="form-actions">
        <button type="button" className="link-button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="submit" className="primary" disabled={busy}>
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Date helpers — `datetime-local` inputs round-trip without a timezone, so we
// convert in/out of ISO-Z explicitly.
// ---------------------------------------------------------------------------

function toInputDateTime(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    d.getFullYear() +
    "-" +
    pad(d.getMonth() + 1) +
    "-" +
    pad(d.getDate()) +
    "T" +
    pad(d.getHours()) +
    ":" +
    pad(d.getMinutes())
  );
}

function toIsoZ(local: string): string {
  // `datetime-local` value is in the user's local zone but expressed without
  // an offset. Pass it through Date() to normalize to ISO-Z.
  if (!local) return new Date().toISOString();
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return new Date().toISOString();
  return d.toISOString();
}

function safeParseDate(value: string): Date {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? new Date() : d;
}
